// Types for Goals Map Interface - Supabase Integration
// Database fields are in English, UI will handle translations

export type GoalStatus = 'planned' | 'in_progress' | 'completed' | 'with_mentor' | 'follow_up' | 'archived';
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

// Main Goal interface - matches Supabase table structure
export interface Goal {
  id: string;
  user_id: string;
  tenant_id: string;
  
  // Goal fields (in English for database)
  title: string;
  life_area?: string;
  current_state?: string;
  future_goal?: string;
  target_date?: string; // ISO date string
  start_date?: string; // ISO date string
  budget?: string;
  responsible?: string;
  who_can_help?: string;
  how_will_be_done?: string;
  measures?: string;
  observations?: string;
  
  // Status and metadata
  status: GoalStatus;
  created_at: string;
  updated_at: string;
}

// Goal Task interface - matches Supabase table structure
export interface GoalTask {
  id: string;
  goal_id: string;
  user_id: string;
  tenant_id: string;
  
  // Task fields (in English for database)
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  
  // Dates
  due_date?: string; // ISO date string
  completion_date?: string; // ISO date string
  
  // Metadata
  created_at: string;
  updated_at: string;
}

// Goal Status History interface
export interface GoalStatusHistory {
  id: string;
  goal_id: string;
  user_id: string;
  tenant_id: string;
  
  // History fields
  previous_status?: string;
  new_status: string;
  reason?: string;
  notes?: string;
  
  // Metadata
  created_at: string;
}

// UI-specific interfaces for form handling
export interface GoalFormData {
  title: string;
  life_area: string;
  current_state: string;
  future_goal: string;
  target_date: string;
  start_date: string;
  budget: string;
  responsible: string;
  who_can_help: string;
  how_will_be_done: string;
  measures: string;
  observations: string;
  related_tasks: string[]; // Array of task titles
}

// Filter and sort options
export interface GoalFilters {
  status?: GoalStatus;
  life_area?: string;
  date_range?: {
    start: string;
    end: string;
  };
}

export interface GoalSortOptions {
  field: 'title' | 'target_date' | 'status' | 'created_at';
  direction: 'ASC' | 'DESC';
}

// API response types
export interface GoalsResponse {
  data: Goal[];
  count: number;
  error?: string;
}

export interface GoalResponse {
  data: Goal | null;
  error?: string;
}

export interface GoalsStats {
  total_goals: number;
  planned_goals: number;
  in_progress_goals: number;
  completed_goals: number;
  with_mentor_goals: number;
  follow_up_goals: number;
  archived_goals: number;
}

// Status mapping for UI translation
export const STATUS_MAPPING = {
  pt: {
    planned: 'Planejada',
    in_progress: 'Em Progresso',
    completed: 'Concluída',
    with_mentor: 'Com Mentor',
    follow_up: 'Acompanhamento',
    archived: 'Arquivada'
  },
  en: {
    planned: 'Planned',
    in_progress: 'In Progress',
    completed: 'Completed',
    with_mentor: 'With Mentor',
    follow_up: 'Follow-up',
    archived: 'Archived'
  }
} as const;

// Field mapping for UI translation
export const FIELD_MAPPING = {
  pt: {
    title: 'Título',
    life_area: 'Área da Vida',
    current_state: 'Estado Atual',
    future_goal: 'Meta Futura',
    target_date: 'Data Alvo',
    start_date: 'Data de Início',
    budget: 'Orçamento',
    responsible: 'Responsável',
    who_can_help: 'Quem Pode Ajudar',
    how_will_be_done: 'Como Será Feito',
    measures: 'Medidas',
    observations: 'Observações'
  },
  en: {
    title: 'Title',
    life_area: 'Life Area',
    current_state: 'Current State',
    future_goal: 'Future Goal',
    target_date: 'Target Date',
    start_date: 'Start Date',
    budget: 'Budget',
    responsible: 'Responsible',
    who_can_help: 'Who Can Help',
    how_will_be_done: 'How Will Be Done',
    measures: 'Measures',
    observations: 'Observations'
  }
} as const;

// Utility functions for status translation
export const getStatusLabel = (status: GoalStatus, language: 'pt' | 'en' = 'pt'): string => {
  return STATUS_MAPPING[language][status] || status;
};

export const getFieldLabel = (field: keyof typeof FIELD_MAPPING.pt, language: 'pt' | 'en' = 'pt'): string => {
  return FIELD_MAPPING[language][field] || field;
};

// Convert localStorage data to Supabase format
export const convertLocalStorageToSupabase = (localData: any): Goal => {
  return {
    id: localData.id || '',
    user_id: '', // Will be set by the hook
    tenant_id: 'major-high',
    title: localData.titulo || '',
    life_area: localData.areaVida || '',
    current_state: localData.estadoAtual || '',
    future_goal: localData.metaFutura || '',
    target_date: localData.dataAlvo || '',
    start_date: localData.dataInicio || '',
    budget: localData.orcamento || '',
    responsible: localData.responsavel || '',
    who_can_help: localData.quemPodeAjudar || '',
    how_will_be_done: localData.comoSeraFeito || '',
    measures: localData.medidas || '',
    observations: localData.observacoes || '',
    status: mapStatusToSupabase(localData.status || 'planejado'),
    created_at: localData.createdAt || new Date().toISOString(),
    updated_at: localData.updatedAt || new Date().toISOString()
  };
};

// Convert Supabase data to localStorage format (for compatibility)
export const convertSupabaseToLocalStorage = (supabaseData: Goal): any => {
  console.log('🔄 Convertendo dados do Supabase para localStorage:', {
    id: supabaseData.id,
    target_date: supabaseData.target_date,
    start_date: supabaseData.start_date,
    title: supabaseData.title
  });
  
  const converted = {
    id: supabaseData.id,
    titulo: supabaseData.title,
    areaVida: supabaseData.life_area,
    estadoAtual: supabaseData.current_state,
    metaFutura: supabaseData.future_goal,
    dataAlvo: supabaseData.target_date || null, // Preservar null se vazio
    dataInicio: supabaseData.start_date || null, // Preservar null se vazio
    orcamento: supabaseData.budget,
    responsavel: supabaseData.responsible,
    quemPodeAjudar: supabaseData.who_can_help,
    comoSeraFeito: supabaseData.how_will_be_done,
    medidas: supabaseData.measures,
    observacoes: supabaseData.observations,
    status: mapStatusToLocalStorage(supabaseData.status),
    createdAt: supabaseData.created_at,
    updatedAt: supabaseData.updated_at
  };
  
  console.log('✅ Dados convertidos:', {
    id: converted.id,
    dataAlvo: converted.dataAlvo,
    dataInicio: converted.dataInicio
  });
  
  return converted;
};

// Status mapping functions
export const mapStatusToSupabase = (localStatus: string): GoalStatus => {
  const mapping: Record<string, GoalStatus> = {
    'planejado': 'planned',
    'em_progresso': 'in_progress',
    'concluido': 'completed',
    'com_mentoria': 'with_mentor',
    'acompanhamento': 'follow_up',
    'arquivado': 'archived'
  };
  return mapping[localStatus] || 'planned';
};

export const mapStatusToLocalStorage = (supabaseStatus: GoalStatus): string => {
  const mapping: Record<GoalStatus, string> = {
    'planned': 'planejado',
    'in_progress': 'em_progresso',
    'completed': 'concluido',
    'with_mentor': 'com_mentoria',
    'follow_up': 'acompanhamento',
    'archived': 'arquivado'
  };
  return mapping[supabaseStatus] || 'planejado';
};
